import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { List, Pencil, Plus } from 'lucide-react';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import LoadingState from '../components/LoadingState';
import StatusBadge from '../components/StatusBadge';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import SearchInput from '../components/ui/SearchInput';
import SegmentedControl from '../components/ui/SegmentedControl';
import { EmptyState, ErrorState } from '../components/ui/States';
import { CreditNote, CreditNoteType, listCreditNotes } from '../lib/creditNoteApi';
import { formatDate as fmtDate } from "../utils/dateFormat";
import { formatCurrency } from '../utils/priceUtils';
import { VendorReturnsList } from '../features/debitNotes/VendorReturnsList';

const INFO = (
  <div className="flex flex-col gap-3">
    <p>Credit notes reduce what a client owes — item returns, discounts, or corrections against a client invoice.</p>
    <p className="font-medium">Every note has exactly one type:</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li>Item Return — negative line items for goods sent back</li>
      <li>Discount — a % off one or more linked invoices, netted against prior credits</li>
      <li>Correction — negative line items reversing a billing mistake</li>
    </ul>
    <p>All amounts on a credit note are negative, in the note&apos;s currency.</p>
    <p className="font-medium">Vendor tab</p>
    <p>
      Vendor returns (DN) send goods back to a supplier or claim money off one of their invoices. An Admin approves
      each one before the supplier's bill is credited; the supplier's own credit note is attached to it when it arrives.
    </p>
  </div>
);

type Side = 'client' | 'vendor';

const SIDES: Array<{ value: Side; label: string }> = [
  { value: 'client', label: 'Client' },
  { value: 'vendor', label: 'Vendor' },
];


function formatDate(value?: string) {
  return fmtDate(value, '--');
}

// The status pill and its colour map moved to components/StatusBadge, so this
// screen and every other one classify a status the same way (docs/DESIGN.md).

/** Never the raw error: the list either loaded or it did not, and the user's
 *  next move is the same either way. */
const LOAD_FAILED = 'Credit notes could not be loaded. Check your connection and try again.';

function typeLabel(type: string) {
  if (type === 'item_return') return 'Item Return';
  if (type === 'discount') return 'Discount';
  if (type === 'correction') return 'Correction';
  return String(type || '--');
}

function typeClass(type: string) {
  if (type === 'item_return') return 'bg-sky-50 text-sky-700';
  if (type === 'discount') return 'bg-purple-50 text-purple-700';
  if (type === 'correction') return 'bg-amber-50 text-amber-700';
  return 'bg-slate-100 text-slate-700';
}

// The type chip is NOT a status: it is the note's kind, and StatusBadge would
// classify "correction" as an alarm. It keeps its own neutral palette.
const TYPE_FILTERS: Array<{ value: '' | CreditNoteType; label: string }> = [
  { value: '', label: 'All' },
  { value: 'item_return', label: 'Item Return' },
  { value: 'discount', label: 'Discount' },
  { value: 'correction', label: 'Correction' },
];

export const CreditNotesPage: React.FC = () => {
  const navigate = useNavigate();
  // The tab lives in the URL so "Back" from a vendor return lands on the Vendor tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const side: Side = searchParams.get('tab') === 'vendor' ? 'vendor' : 'client';
  const setSide = (next: Side) => setSearchParams(next === 'vendor' ? { tab: 'vendor' } : {}, { replace: true });
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<CreditNote[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [type, setType] = useState<'' | CreditNoteType>('');
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listCreditNotes({ page, limit, search, type: type || undefined });
      setItems(res.data || []);
      setTotal(res.total || 0);
      setError(null);
    } catch {
      // A failed request used to render the same "No credit notes found." an
      // empty result does, so an outage read as a clean register. The two are
      // now told apart: an error gets a retry, an empty list gets a next step.
      setItems([]);
      setTotal(0);
      setError(LOAD_FAILED);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // The Vendor tab loads its own list; the client one waits until it is shown.
    if (side !== 'client') return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, type, side]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      if (side !== 'client') return;
      if (page !== 1) {
        setPage(1);
      } else {
        load();
      }
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* The toggle sits with the title, so it stays put when the actions on the right change. */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 shrink-0">
              <h1 className="text-xl font-semibold leading-7 text-slate-900">Credit Notes</h1>
              <ModuleInfoButton content={INFO} ariaLabel="Credit note information" />
            </div>
            <SegmentedControl aria-label="Client or vendor" value={side} onChange={setSide} segments={SIDES} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {side === 'client' ? (
              <>
                <Button
                  onClick={() => navigate('/credit-notes/register')}
                  icon={<List aria-hidden="true" className="h-4 w-4" />}
                >
                  Register
                </Button>
                <Button
                  variant="primary"
                  onClick={() => navigate('/credit-notes/new')}
                  icon={<Plus aria-hidden="true" className="h-4 w-4" />}
                >
                  New credit note
                </Button>
              </>
            ) : (
              <Button
                variant="primary"
                onClick={() => navigate('/credit-notes/vendor/new')}
                icon={<Plus aria-hidden="true" className="h-4 w-4" />}
              >
                New vendor return
              </Button>
            )}
          </div>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          {side === 'client'
            // Item 38 (9 Oct 2026 QA retest): this used to claim "Every value here is negative" —
            // false the moment a draft with nothing costed yet, or a cancelled note, shows 0.00;
            // the Status column beside the amount already says which (draft/issued/cancelled).
            ? 'Item returns, discounts, and corrections against client invoices. A credited amount is negative; an unpriced draft shows 0.00.'
            : 'Goods United returns to a supplier, and money claimed off a supplier invoice.'}
        </p>
      </div>

      <div className="px-4 py-4 flex-1 overflow-auto sm:px-8">
        {side === 'vendor' ? (
          <VendorReturnsList />
        ) : (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
            <SegmentedControl
              aria-label="Credit note type"
              value={type}
              onChange={setType}
              segments={TYPE_FILTERS.map((f) => ({ value: f.value, label: f.label }))}
            />
            <SearchInput value={search} onChange={setSearch} placeholder="Search here" className="w-48" />
          </div>

          {error ? (
            <div className="p-4">
              <ErrorState message={error} onRetry={load} />
            </div>
          ) : (
            <div className="overflow-auto">
              {/* `data-table` (index.css) supplies the header, cell padding and row
                  hover, so only genuine per-cell overrides stay. */}
              <table className="data-table min-w-[1100px]">
                <thead>
                  <tr>
                    <th>CR number</th>
                    <th>Type</th>
                    <th>Vessel</th>
                    <th>Client</th>
                    <th>Date</th>
                    <th className="text-right">Grand total</th>
                    <th>Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={8} className="py-8">
                        <LoadingState text="Loading credit notes..." />
                      </td>
                    </tr>
                  )}
                  {!loading && items.length === 0 && (
                    <tr>
                      <td colSpan={8} className="py-10">
                        <EmptyState
                          title="No credit notes found"
                          detail="Use New credit note to create an item return, discount, or correction."
                        />
                      </td>
                    </tr>
                  )}
                  {items.map((item) => (
                    <tr
                      key={item._id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/credit-notes/${item._id}`)}
                    >
                      <td className="font-medium text-slate-900">{item.creditNoteNumber}</td>
                      <td>
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${typeClass(item.type)}`}
                        >
                          {typeLabel(item.type)}
                        </span>
                      </td>
                      <td>{item.vesselName || '--'}</td>
                      <td>{item.clientName || '--'}</td>
                      {/* D61 — the chosen day first, then the same fallback the
                          printed note and the register use. */}
                      <td>{formatDate(item.creditNoteDate || item.issuedAt || item.createdAt)}</td>
                      <td className="text-right font-semibold tabular-nums text-red-700">
                        {/* D96.17 — in the note's own currency, never a guessed US$. */}
                        {formatCurrency(Number(item.grandTotal || 0), item.currency || null, { ceil: false })}
                      </td>
                      <td>
                        <StatusBadge status={item.status || 'draft'} />
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/credit-notes/${item._id}`);
                            }}
                          >
                            View
                          </Button>
                          <Button
                            size="sm"
                            aria-label={`Edit credit note ${item.creditNoteNumber}`}
                            icon={<Pencil aria-hidden="true" className="h-4 w-4" />}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/credit-notes/${item._id}`);
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination page={page} pageSize={limit} total={total} onPageChange={setPage} noun="credit notes" />
        </div>
        )}
      </div>
    </div>
  );
};

export default CreditNotesPage;
